/**
 * Terms of Use — public route, no auth required.
 *
 * Serves the launch-approved terms content directly from the SPA so that
 * the App Store 3.1.2 requirement ("terms of use accessible without an
 * account for any auto-renewable subscription") is met on
 * `app.futureself.es/terms`. Deliberately unauthenticated.
 *
 * Content is the authoritative launch copy referenced in
 * /app/memory/LAUNCH_CHECKLIST.md §12.2 (2026-07-25).
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import useSmartBack from '../lib/useSmartBack';

const LAST_UPDATED = '29 July 2026';

const Section = ({ title, children }) => (
  <section className="mb-8" data-testid={`terms-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <h2 className="font-serif text-2xl text-[#DCD3CA] mb-3">{title}</h2>
    <div className="text-[#B5B1AB] font-sans text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const Terms = () => {
  const smartBack = useSmartBack();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof document !== 'undefined') {
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }
  }, []);
  return (
  <div
    className="min-h-[100dvh] bg-[#1C1C1E] text-[#DCD3CA]"
    style={{
      paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
      paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
    }}
    data-testid="terms-page"
  >
    <div className="max-w-2xl mx-auto px-6">
      <button
        type="button"
        onClick={smartBack}
        className="inline-flex items-center gap-2 text-[#8BAA9E] font-sans text-sm mb-8 hover:text-[#A3C2B6]"
        data-testid="terms-back-link"
        aria-label="Back"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Future Self
      </button>

      <header className="mb-10">
        <p className="text-[#8BAA9E] font-sans text-xs uppercase tracking-[2px] mb-3">FutureSelf</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F5F3EF] mb-3">Terms of Use</h1>
        <p className="text-[#7A7670] font-sans text-xs">Last updated: {LAST_UPDATED}</p>
      </header>

      <Section title="Acceptance">
        <p>
          By creating an account or using Future Self, you agree to these Terms of Use. If you do not agree,
          please do not use the app.
        </p>
      </Section>

      <Section title="What Future Self Is">
        <p>
          Future Self is a personal-reflection app for identity-based habit change, daily visualisation and
          journaling. It is designed for individual use. It is not a medical, therapeutic or clinical
          service.
        </p>
      </Section>

      <Section title="Your Content">
        <p>
          Future Self is a personal-reflection app. You may enter journal entries, gratitude notes, goals,
          identity statements and upload background photos or videos. This content is stored solely for your
          own private use inside your account and is not published, shared with, or made visible to other
          users. Future Self does not host any public user-generated content, does not offer any social feed,
          and does not moderate user content beyond automated safety scanning of the media you upload for
          your own backgrounds.
        </p>
      </Section>

      <Section title="Premium Subscription">
        <p>
          Premium is an auto-renewing subscription that unlocks unlimited AI-generated affirmations, unlimited
          custom visualisation backgrounds, and voice narration.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Payment is charged to your Apple ID, Google account or Stripe-billed card at confirmation of purchase.</li>
          <li>The subscription automatically renews each period unless cancelled at least 24 hours before the end of the current period.</li>
          <li>You may cancel at any time in your Apple / Google account settings, or via <Link to="/settings" className="text-[#8BAA9E]">Settings</Link> on the web.</li>
          <li>No refund is offered for the unused portion of any billed period, except where required by law.</li>
        </ul>
      </Section>

      <Section title="Acceptable Use">
        <p>
          You agree not to reverse-engineer, attempt to bypass any security controls, or upload media that
          you do not have the legal right to use. We may suspend accounts that materially violate this
          section or applicable law.
        </p>
      </Section>

      <Section title="Availability & Changes">
        <p>
          We provide the app &ldquo;as is.&rdquo; We may update features, remove features, or take the service offline
          for maintenance. We will make reasonable efforts to notify you of any material changes to these
          Terms before they take effect.
        </p>
      </Section>

      <Section title="Liability">
        <p>
          To the fullest extent permitted by law, Future Self is not liable for indirect, incidental, or
          consequential damages arising from your use of the app. Nothing in these Terms limits our
          liability for gross negligence or wilful misconduct.
        </p>
      </Section>

      <Section title="Governing Law">
        <p>
          These Terms are governed by the laws of Ireland, and any dispute not resolved amicably shall be
          subject to the exclusive jurisdiction of the courts of Ireland. If you are a consumer resident in
          the EU, you retain the protections of the mandatory consumer laws of your country of residence.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions or notice under these Terms: <span className="text-[#DCD3CA]">support@futureself.es</span>.
        </p>
      </Section>

      <footer className="pt-8 mt-8 border-t border-white/5 text-[#7A7670] font-sans text-xs">
        <p>Future Self · Made with care in Ireland.</p>
        <p className="mt-2">
          See also our{' '}
          <Link to="/privacy" className="text-[#8BAA9E] hover:text-[#A3C2B6]" data-testid="terms-to-privacy-link">
            Privacy Policy
          </Link>
          .
        </p>
      </footer>
    </div>
  </div>
  );
};

export default Terms;
